import { useMemo } from 'react';

import { createHook } from 'react-sweet-state';

import { RovodalStore } from './store-internal';
import {
	DEFAULT_INSTANCE_ID,
	type RovodalEntry,
	type RovodalInstanceId,
	type RovodalKey,
	type RovodalStateFor,
	type RovodalRegisteredKey,
	type RovodalStoreState,
} from './types';

const useRovodalEntryHook = createHook(RovodalStore, {
	selector: (
		state: RovodalStoreState,
		{ key, instanceId }: { key: RovodalKey; instanceId: RovodalInstanceId },
	): RovodalEntry | undefined => state.byKey[key]?.[instanceId],
});

/** `key`(+`instanceId`)-bound, consumer-typed action facade from `useRovodal`. */
export type RovodalKeyedActions<TState> = {
	open: (state?: TState) => void;
	close: () => void;
	setState: (state: TState) => void;
	remove: () => void;
};

export function useRovodal<K extends RovodalRegisteredKey>(
	key: K,
	instanceId?: RovodalInstanceId,
): [RovodalEntry<RovodalStateFor<K>, K> | undefined, RovodalKeyedActions<RovodalStateFor<K>>];

export function useRovodal<TState = unknown>(
	key: RovodalKey,
	instanceId?: RovodalInstanceId,
): [RovodalEntry<TState> | undefined, RovodalKeyedActions<TState>];

export function useRovodal<TState = unknown>(
	key: RovodalKey,
	instanceId: RovodalInstanceId = DEFAULT_INSTANCE_ID,
): [RovodalEntry<TState> | undefined, RovodalKeyedActions<TState>] {
	const [entry, boundActions] = useRovodalEntryHook({ key, instanceId });
	const keyedActions = useMemo<RovodalKeyedActions<TState>>(
		() => ({
			open: (state) => boundActions.open(key, instanceId, state),
			close: () => boundActions.close(key, instanceId),
			setState: (state) => boundActions.setState(key, instanceId, state),
			remove: () => boundActions.remove(key, instanceId),
		}),
		[boundActions, key, instanceId],
	);
	return [entry as RovodalEntry<TState> | undefined, keyedActions];
}
